/**
 * Library API calls
 */

import apiClient from './client';
import {
  Library,
  LibraryAlias,
  LibrarySection,
  LibraryStats,
  CreateLibraryRequest,
  UpdateLibraryRequest,
  CreateLibraryAliasRequest,
  UpdateLibraryAliasRequest,
  CreateLibrarySectionRequest,
  UpdateLibrarySectionRequest,
  LibrarySuggestionRequest,
  LibrarySuggestionResponse,
} from '@/types/api';

// ============================================================================
// Libraries
// ============================================================================

/**
 * Get all libraries
 */
export const getLibraries = async (): Promise<Library[]> => {
  const response = await apiClient.get<Library[]>('/libraries');
  return response.data;
};

/**
 * Get single library by ID
 */
export const getLibrary = async (id: number): Promise<Library> => {
  const response = await apiClient.get<Library>(`/libraries/${id}`);
  return response.data;
};

/**
 * Create new library
 */
export const createLibrary = async (data: CreateLibraryRequest): Promise<Library> => {
  const response = await apiClient.post<Library>('/libraries', data);
  return response.data;
};

/**
 * Update existing library
 */
export const updateLibrary = async (id: number, data: UpdateLibraryRequest): Promise<Library> => {
  const response = await apiClient.put<Library>(`/libraries/${id}`, data);
  return response.data;
};

/**
 * Delete library
 */
export const deleteLibrary = async (id: number): Promise<void> => {
  await apiClient.delete(`/libraries/${id}`);
};

/**
 * Get library statistics
 */
export const getLibraryStats = async (id: number): Promise<LibraryStats> => {
  const response = await apiClient.get<LibraryStats>(`/libraries/${id}/stats`);
  return response.data;
};

/**
 * Generate suggestions for a library
 * Note: This is a long-running operation that may take several minutes
 */
export const generateLibrarySuggestions = async (
  libraryId: number,
  data: LibrarySuggestionRequest
): Promise<LibrarySuggestionResponse> => {
  const response = await apiClient.post<LibrarySuggestionResponse>(
    `/libraries/${libraryId}/suggestions/generate`,
    data,
    {
      timeout: 300000, // 5 minutes for long-running suggestion generation
    }
  );
  return response.data;
};

// ============================================================================
// Library Aliases
// ============================================================================

/**
 * Get all aliases for a library
 */
export const getLibraryAliases = async (libraryId: number): Promise<LibraryAlias[]> => {
  const response = await apiClient.get<LibraryAlias[]>(`/libraries/${libraryId}/aliases`);
  return response.data;
};

/**
 * Create new library alias
 */
export const createLibraryAlias = async (
  libraryId: number,
  data: CreateLibraryAliasRequest
): Promise<LibraryAlias> => {
  const response = await apiClient.post<LibraryAlias>(`/libraries/${libraryId}/aliases`, data);
  return response.data;
};

/**
 * Delete library alias
 */
export const deleteLibraryAlias = async (libraryId: number, aliasId: number): Promise<void> => {
  await apiClient.delete(`/libraries/${libraryId}/aliases/${aliasId}`);
};

/**
 * Update existing library alias
 */
export const updateLibraryAlias = async (
  libraryId: number,
  aliasId: number,
  data: UpdateLibraryAliasRequest
): Promise<LibraryAlias> => {
  const response = await apiClient.put<LibraryAlias>(
    `/libraries/${libraryId}/aliases/${aliasId}`,
    data
  );
  return response.data;
};

// ============================================================================
// Library Sections
// ============================================================================

/**
 * Get all sections for a library
 */
export const getLibrarySections = async (libraryId: number): Promise<LibrarySection[]> => {
  const response = await apiClient.get<LibrarySection[]>(`/libraries/${libraryId}/sections`);
  return response.data;
};

/**
 * Create new library section
 */
export const createLibrarySection = async (
  libraryId: number,
  data: CreateLibrarySectionRequest
): Promise<LibrarySection> => {
  const response = await apiClient.post<LibrarySection>(`/libraries/${libraryId}/sections`, data);
  return response.data;
};

/**
 * Update library section
 */
export const updateLibrarySection = async (
  libraryId: number,
  sectionId: number,
  data: UpdateLibrarySectionRequest
): Promise<LibrarySection> => {
  const response = await apiClient.put<LibrarySection>(
    `/libraries/${libraryId}/sections/${sectionId}`,
    data
  );
  return response.data;
};

/**
 * Delete library section
 */
export const deleteLibrarySection = async (libraryId: number, sectionId: number): Promise<void> => {
  await apiClient.delete(`/libraries/${libraryId}/sections/${sectionId}`);
};
